
import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { checkUserApprovalStatus, checkAdminStatus } from '@/utils/authHelpers';

interface AuthContextProps {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isApprovedMember: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
  checkIsAdmin: () => Promise<boolean>;
  checkIsApprovedMember: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextProps>({
  session: null,
  user: null,
  isAdmin: false,
  isApprovedMember: false,
  isLoading: true,
  signOut: async () => {},
  checkIsAdmin: async () => false,
  checkIsApprovedMember: async () => false,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApprovedMember, setIsApprovedMember] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Simplified admin check using helper
  const checkIsAdmin = async (): Promise<boolean> => {
    if (!user) {
      console.log('[AuthContext] checkIsAdmin: No user, returning false.');
      setIsAdmin(false);
      return false;
    }
    
    try {
      const adminStatus = await checkAdminStatus(user.id, user.email || '');
      setIsAdmin(adminStatus);
      return adminStatus;
    } catch (error) {
      console.error('[AuthContext] checkIsAdmin: Exception:', error);
      setIsAdmin(false);
      return false;
    }
  };

  // Simplified approval check using helper
  const checkIsApprovedMember = async (): Promise<boolean> => {
    if (!user) {
      console.log('[AuthContext] checkIsApprovedMember: No user, returning false.');
      setIsApprovedMember(false);
      return false;
    }
    
    try {
      const approvalStatus = await checkUserApprovalStatus(user.id, user.email || '');
      setIsApprovedMember(approvalStatus);
      return approvalStatus;
    } catch (error) {
      console.error('[AuthContext] checkIsApprovedMember: Exception:', error);
      setIsApprovedMember(false);
      return false;
    }
  };

  // Sign out function
  const signOut = async () => {
    console.log('[AuthContext] signOut: Signing out user.');
    try {
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsApprovedMember(false);
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[AuthContext] signOut: Error during sign out:', error);
        throw error;
      }
      console.log('[AuthContext] signOut: Sign out successful.');
    } catch (error) {
      console.error('[AuthContext] signOut: Exception during sign out:', error);
      throw error; 
    }
  };

  useEffect(() => {
    console.log('[AuthContext] Initializing Auth. Setting isLoading = true.');
    setIsLoading(true);

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('[AuthContext] onAuthStateChange event:', event, 'session:', currentSession);
        setSession(currentSession);
        const currentUser = currentSession?.user ?? null;
        setUser(currentUser);
        
        if (currentUser) {
          console.log('[AuthContext] User found from onAuthStateChange. Checking roles...');
          // Use setTimeout to avoid blocking the auth state change
          setTimeout(async () => {
            try {
              await Promise.all([
                checkIsAdmin(),
                checkIsApprovedMember()
              ]);
            } catch (error) {
              console.error('[AuthContext] Error checking roles:', error);
            }
          }, 100);
        } else {
          console.log('[AuthContext] No user from onAuthStateChange. Resetting admin/approved status.');
          setIsAdmin(false);
          setIsApprovedMember(false);
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      console.log('[AuthContext] getSession result:', currentSession);
      setSession(currentSession);
      const currentUser = currentSession?.user ?? null;
      setUser(currentUser);
      
      if (currentUser) {
        console.log('[AuthContext] User found from getSession. Checking roles...');
        try {
          await Promise.all([
            checkIsAdmin(),
            checkIsApprovedMember()
          ]);
        } catch (error) {
          console.error('[AuthContext] Error checking roles during init:', error);
        }
      }
      
      console.log('[AuthContext] Setting isLoading = false.');
      setIsLoading(false);
    }).catch(error => {
      console.error('[AuthContext] Error in getSession promise:', error);
      setIsLoading(false);
    });

    return () => {
      console.log('[AuthContext] Unsubscribing from onAuthStateChange.');
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      isAdmin, 
      isApprovedMember, 
      isLoading, 
      signOut, 
      checkIsAdmin, 
      checkIsApprovedMember 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
